import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import { StudioDataBridge, type StudioDraft } from '../components/StudioDataBridge';
import { supabase } from '../lib/supabase';
import { MakeAnythingStudio } from '../components/MakeAnythingStudio';
import { ImageStudio } from '../components/ImageStudio';
import {
  fetchCityRankedVenues,
  fetchRatedCities,
  formatScore,
  venueDisplayName,
  type GrowthCity,
  type GrowthVenue,
} from '../lib/growth';

type StudioMode =
  | 'social' | 'city-top10' | 'signage' | 'appstore' | 'reel' | 'flyer' | 'sticker' | 'flag' | 'tent' | 'qr' | 'logo' | 'make' | 'images' | 'library' | 'venueposts';

/** A studio_assets row whose `prompt` is "studio-draft:<studio>" — the Library tab's unit. */
type LibraryAsset = {
  id: string;
  title: string;
  prompt: string;
  html: string;
  width: number;
  height: number;
  updated_at: string;
  created_by?: string | null;
};
const DRAFT_STUDIOS = new Set(['flyer', 'sticker', 'signage', 'flag', 'tent', 'qr', 'venueposts']);

/**
 * TWO EMBEDDED CANVAS EDITORS, ONE RENDERING APPROACH.
 *
 * · public/growth/appstore-series.html — the standalone App Store series editor Evan liked,
 *   vendored verbatim (self-contained: Clash Display inlined as data URIs, zero network calls).
 * · public/growth/social-studio.html — the six-format social studio (leaderboard, venue card,
 *   head-to-head, milestone, stamp, quote), built on the SAME canvas foundation: the helpers
 *   `appstore-series.html` inlines are extracted to public/growth/shado-canvas.js and its fonts
 *   to public/growth/clash-fonts.css, so both tools measure and draw identically.
 *
 * Don't reimplement either in React — edit the vendored file. React's job here is data: the
 * social studio has no Supabase key by design, so <StudioDataBridge> answers its postMessage
 * requests with live cities, venues, breakdowns and counts. See that component for the protocol.
 *
 * City/venue assets are text-only by design: Google/Foursquare photos can't be reused in
 * marketing, and pure typography exports cleanly (no CORS taint). The social studio's optional
 * photo backdrop is a file YOU drop in, never a fetched venue photo.
 */
/**
 * ⚠️ CACHE-BUSTED. These are STATIC files under `public/`, so the browser caches the HTML and an
 * edit to the canvas code can keep rendering the old output long after it deployed — which is
 * exactly what happened with the screenshot-crop fix on 2026-08-05. Bump `STUDIO_ASSET_VERSION`
 * whenever either studio's drawing code changes.
 */
const STUDIO_ASSET_VERSION = '2026-09-22c';
const APPSTORE_EDITOR_PATH = `/growth/appstore-series.html?v=${STUDIO_ASSET_VERSION}`;
const SOCIAL_STUDIO_PATH = `/growth/social-studio.html?v=${STUDIO_ASSET_VERSION}`;
/** Instagram carousel builder — slides with a clip record to video, text-only slides export PNG. */
const REEL_STUDIO_PATH = `/growth/reel-studio.html?v=${STUDIO_ASSET_VERSION}`;
/**
 * Print pieces — posters, table tents, quarter sheets, stickers. Same embedded-studio pattern as the
 * reel and social tools, but it works in INCHES x 300 DPI rather than the 1290px screen canvas the
 * others use, because paper does not care about pixels.
 */
const FLYER_STUDIO_PATH = `/growth/flyer-studio.html?v=${STUDIO_ASSET_VERSION}`;
/** 3×3in sticker editor — same engine as the flyer studio, sticker-only template gallery. */
const STICKER_STUDIO_PATH = `/growth/sticker-studio.html?v=${STUDIO_ASSET_VERSION}`;
/** Venue-facing print pieces (counter card, window decal, rank poster…) — flyer engine, venue-first templates. */
const SIGNAGE_STUDIO_PATH = `/growth/venue-signage-studio.html?v=${STUDIO_ASSET_VERSION}`;
/** Fabric formats — 3×5 flags, vertical drops, long banners — same engine, flag-size toggles. */
const FLAG_STUDIO_PATH = `/growth/flag-studio.html?v=${STUDIO_ASSET_VERSION}`;
/** Bare QR generator — every knob exposed, PNG at any px + true-vector SVG. For matchboxes etc. */
const QR_STUDIO_PATH = `/growth/qr-studio.html?v=${STUDIO_ASSET_VERSION}`;
/** 18-up 6.0×4.2cm cut-out cards on a US-letter sheet — edit card 1, copy to all, print, cut. */
const TENT_STUDIO_PATH = `/growth/tent-studio.html?v=${STUDIO_ASSET_VERSION}`;
/** The traced vector wordmark alone — color, background, spacing, export at any quality. */
const LOGO_STUDIO_PATH = `/growth/logo-studio.html?v=${STUDIO_ASSET_VERSION}`;

function Mark({ size = 12 }: { size?: number }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className="rounded-[3.5px] bg-accent"
        style={{ width: size, height: size, boxShadow: '0 0 0 3px rgba(139,92,246,0.14)' }}
      />
      <span className="font-display font-semibold tracking-[-0.02em] text-white" style={{ fontSize: size + 5 }}>
        Shado
      </span>
    </span>
  );
}

/** Tap-to-edit text on the export canvas — same in-place editing the series editor has. */
function Editable({
  children,
  className,
  editKey,
}: {
  children: React.ReactNode;
  className?: string;
  editKey?: string;
}) {
  return (
    <div
      key={editKey}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      className={`${className ?? ''} outline-none focus:rounded-sm focus:ring-1 focus:ring-accent/60`}
    >
      {children}
    </div>
  );
}

function CityTop10Card({
  city,
  venues,
  innerRef,
}: {
  city: GrowthCity;
  venues: GrowthVenue[];
  innerRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={innerRef}
      className="relative overflow-hidden bg-[#08090A]"
      style={{ width: 360, height: 640 }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(130% 55% at 50% -8%, rgba(139,92,246,0.30), transparent 62%)' }}
      />
      <div className="relative flex h-full flex-col px-7 pb-6 pt-7">
        <div className="flex items-center justify-between">
          <Mark />
          <span className="text-[8px] font-semibold uppercase tracking-[0.18em] text-[#9A9AA2]">
            Nightlife · ranked
          </span>
        </div>

        <div className="mt-6">
          <Editable
            editKey={city.id}
            className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#b79cff]"
          >
            {city.name ?? 'City'}
          </Editable>
          <Editable className="font-display text-[54px] font-semibold leading-[1.02] tracking-[-0.03em] text-white">
            TOP 10
          </Editable>
          <Editable className="mt-1 text-[10px] font-medium uppercase tracking-[0.14em] text-[#9A9AA2]">
            BARS &amp; CLUBS — RANKED BY STUDENTS
          </Editable>
        </div>

        <div className="mt-5 flex-1 space-y-[7px]">
          {venues.slice(0, 10).map((v, i) => (
            <div key={v.id} className="flex items-center gap-3">
              <span className="w-5 shrink-0 text-right font-display text-[14px] font-semibold tabular-nums text-[#8B5CF6]">
                {i + 1}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-white">
                {venueDisplayName(v)}
              </span>
              <span className="shrink-0 font-display text-[13px] font-semibold tabular-nums text-[#b79cff]">
                {formatScore(v.venue_shado_score)}
              </span>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center justify-between">
            <div>
              <Editable className="text-[11px] font-semibold text-white">Think we’re wrong?</Editable>
              <Editable className="text-[10px] text-[#9A9AA2]">Rate them yourself.</Editable>
            </div>
            <span className="rounded-md bg-[#8B5CF6] px-3 py-1.5 text-[10px] font-semibold text-white">
              Shado — App Store
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function StudioScreen() {
  // Default = Make anything (Evan 2026-10-16).
  const [mode, setMode] = useState<StudioMode>('make');

  const [cities, setCities] = useState<GrowthCity[]>([]);
  const [selectedCityId, setSelectedCityId] = useState('');
  const [cityVenues, setCityVenues] = useState<GrowthVenue[]>([]);
  const [cityLoading, setCityLoading] = useState(true);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const cityCardRef = useRef<HTMLDivElement | null>(null);
  const socialFrameRef = useRef<HTMLIFrameElement | null>(null);
  const flyerFrameRef = useRef<HTMLIFrameElement | null>(null);
  const stickerFrameRef = useRef<HTMLIFrameElement | null>(null);
  const signageFrameRef = useRef<HTMLIFrameElement | null>(null);
  const qrFrameRef = useRef<HTMLIFrameElement | null>(null);
  const flagFrameRef = useRef<HTMLIFrameElement | null>(null);
  const tentFrameRef = useRef<HTMLIFrameElement | null>(null);

  /** A library draft waiting for its studio iframe to say `ready`. */
  const [pendingDraft, setPendingDraft] = useState<StudioDraft | null>(null);
  const [libAssets, setLibAssets] = useState<LibraryAsset[] | null>(null);
  const [libOwners, setLibOwners] = useState<Record<string, string>>({});
  const [libError, setLibError] = useState<string | null>(null);

  const loadLibrary = useCallback(async () => {
    setLibError(null);
    const { data, error } = await supabase.rpc('admin_list_studio_assets');
    if (error) {
      setLibError(error.message);
      return;
    }
    const rows = (Array.isArray(data) ? (data as LibraryAsset[]) : []).filter((a) =>
      a.prompt?.startsWith('studio-draft:'),
    );
    setLibAssets(rows);
    const ids = Array.from(new Set(rows.map((r) => r.created_by).filter((x): x is string => Boolean(x))));
    if (ids.length > 0) {
      const { data: ppl } = await supabase.from('profiles').select('id, username').in('id', ids);
      setLibOwners(
        Object.fromEntries(
          ((ppl ?? []) as { id: string; username: string | null }[])
            .filter((pp) => pp.username)
            .map((pp) => [pp.id, pp.username as string]),
        ),
      );
    }
  }, []);

  useEffect(() => {
    if (mode === 'library') void loadLibrary();
  }, [mode, loadLibrary]);

  const openDraft = useCallback((asset: LibraryAsset) => {
    try {
      const draft = JSON.parse(asset.html) as StudioDraft;
      if (!DRAFT_STUDIOS.has(draft.studio)) throw new Error('unknown studio');
      setPendingDraft(draft);
      setMode(draft.studio as StudioMode);
    } catch {
      setLibError('That saved design could not be opened.');
    }
  }, []);

  /** Parsed once per list load — the payload carries the thumbnail and the source studio. */
  const libItems = useMemo(
    () =>
      (libAssets ?? []).map((asset) => {
        let draft: StudioDraft | null = null;
        try {
          draft = JSON.parse(asset.html) as StudioDraft;
        } catch {
          /* an unreadable row still lists, it just cannot preview or open */
        }
        return { asset, draft };
      }),
    [libAssets],
  );

  const selectedCity = useMemo(
    () => cities.find((c) => c.id === selectedCityId) ?? null,
    [cities, selectedCityId],
  );

  useEffect(() => {
    let mounted = true;
    void fetchRatedCities().then((rows) => {
      if (!mounted) return;
      setCities(rows);
      if (rows.length > 0) setSelectedCityId((prev) => prev || rows[0].id);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedCityId) return;
    let mounted = true;
    setCityLoading(true);
    void fetchCityRankedVenues(selectedCityId, 10).then((rows) => {
      if (!mounted) return;
      setCityVenues(rows);
      setCityLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [selectedCityId]);

  const exportPng = useCallback(async (node: HTMLDivElement | null, filename: string) => {
    if (!node) return;
    (document.activeElement as HTMLElement | null)?.blur?.();
    setExporting(true);
    setExportError(null);
    try {
      const dataUrl = await toPng(node, { pixelRatio: 3, cacheBust: false });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = filename;
      a.click();
    } catch {
      setExportError('Export failed — usually a webfont fetch hiccup. Try once more.');
    } finally {
      setExporting(false);
    }
  }, []);

  const citySlug = (selectedCity?.name ?? 'city').toLowerCase().replace(/[^a-z0-9]+/g, '-');

  const MODES: { id: StudioMode; label: string }[] = [
    { id: 'make', label: 'Make anything' },
    // Image generation's own home (Evan 2026-10-28); 'City Top 10' removed the same day
    // ("get rid of city top 10") — the code stays, only the tab is gone.
    { id: 'images', label: 'Images' },
    { id: 'social', label: 'Social post' },
    { id: 'appstore', label: 'App Store' },
    { id: 'reel', label: 'Post' },
    { id: 'flyer', label: 'Flyer' },
    { id: 'sticker', label: 'Sticker' },
    { id: 'flag', label: 'Flag' },
    { id: 'signage', label: 'Venue signage' },
    { id: 'tent', label: 'Table tent' },
    { id: 'qr', label: 'QR export' },
    { id: 'logo', label: 'Logo export' },
    { id: 'library', label: 'Library' },
  ];

  /** The canvas editors run full-bleed in an iframe; the React cards stay in a 5xl column. */
  const embedded =
    mode === 'appstore' || mode === 'social' || mode === 'reel' || mode === 'flyer' || mode === 'sticker' ||
    mode === 'signage' || mode === 'qr' || mode === 'flag' || mode === 'logo' || mode === 'tent';
  const embedPath =
    mode === 'social'
      ? SOCIAL_STUDIO_PATH
      : mode === 'reel'
        ? REEL_STUDIO_PATH
        : mode === 'flyer'
          ? FLYER_STUDIO_PATH
          : mode === 'sticker'
            ? STICKER_STUDIO_PATH
            : mode === 'signage'
              ? SIGNAGE_STUDIO_PATH
              : mode === 'qr'
                ? QR_STUDIO_PATH
                : mode === 'flag'
                  ? FLAG_STUDIO_PATH
                  : mode === 'logo'
                    ? LOGO_STUDIO_PATH
                    : mode === 'tent'
                      ? TENT_STUDIO_PATH
          : APPSTORE_EDITOR_PATH;

  return (
    <div className={`admin-page mx-auto ${embedded || mode === 'make' || mode === 'images' ? 'max-w-none' : 'max-w-5xl'}`}>
      <PageHeader
        title="Studio"
        subtitle={
          <button
            type="button"
            onClick={() => setMode('library')}
            className="admin-btn admin-btn-secondary admin-btn-sm"
          >
            Library — your saved designs
          </button>
        }
      />

      <div className="mb-5 flex items-end gap-0.5 overflow-x-auto border-b border-borderStrong">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 pb-2.5 pt-1 text-[12.5px] font-medium transition-colors ${
              mode === m.id
                ? 'border-accent text-white'
                : 'border-transparent text-mutedDim hover:text-white'
            }`}
          >
            {m.label}
          </button>
        ))}
        {embedded ? (
          <a
            href={embedPath}
            target="_blank"
            rel="noreferrer"
            className="-mb-px ml-auto whitespace-nowrap border-b-2 border-transparent px-3 pb-2.5 pt-1 text-[12px] text-mutedDim hover:text-white"
          >
            Open full screen ↗
          </a>
        ) : null}
      </div>

      {mode === 'city-top10' ? (
        <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-3">
          <label className="flex items-center gap-2 text-[12px] text-mutedDim">
            City
            <select
              className="admin-input !w-auto !px-2 !text-[12px]"
              value={selectedCityId}
              onChange={(e) => setSelectedCityId(e.target.value)}
            >
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name ?? '—'} · {c.total_shado_ratings ?? 0} ratings
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={exporting}
            onClick={() => void exportPng(cityCardRef.current, `shado-top10-${citySlug}.png`)}
            className="admin-btn admin-btn-primary admin-btn-sm"
          >
            {exporting ? 'Exporting…' : 'Export PNG'}
          </button>
          <span className="text-[11px] text-mutedDim">
            1080 × 1920 — IG story ready · tap any small text on the card to edit it
          </span>
        </div>
      ) : null}

      {exportError ? <div className="admin-banner-error mb-4">{exportError}</div> : null}

      {mode === 'city-top10' ? (
        <div className="admin-card inline-block !p-5">
          {cityLoading ? (
            <SkeletonRows rows={10} />
          ) : selectedCity && cityVenues.length > 0 ? (
            <CityTop10Card city={selectedCity} venues={cityVenues} innerRef={cityCardRef} />
          ) : (
            <EmptyState
              title="No rated venues in this city yet"
              hint="Pick a city with ratings — the graphic renders straight from the live Top 10."
            />
          )}
        </div>
      ) : null}

      {mode === 'signage' ? (
        <>
          <div className="overflow-hidden rounded-xl border border-borderStrong bg-black">
            <iframe
              ref={signageFrameRef}
              src={SIGNAGE_STUDIO_PATH}
              title="Venue signage studio — counter cards, decals, posters"
              className="block h-[80vh] w-full border-0"
            />
          </div>
          <StudioDataBridge iframeRef={signageFrameRef} draft={pendingDraft?.studio === 'signage' ? pendingDraft : null} onDraftDelivered={() => setPendingDraft(null)} />
          <p className="mt-3 max-w-3xl text-[11.5px] leading-relaxed text-mutedDim">
            Venue-facing print pieces — counter card 4×6″, window decal 4×4″, rank poster 8.5×11″,
            rate-us card, and a bar-top strip 8×2.5″. Search a venue in the Live venue data panel and
            every template fills with its real name, rank and score. Same editor as the Flyer studio:
            drag anything, double-click text to rewrite, click once to restyle (highlight part of a
            text to tint just that part); QR style/color/plate/link; Dark / Light / Violet themes.
            Exports print-resolution PNG.
          </p>
        </>
      ) : null}

      {mode === 'social' ? (
        <>
          <div className="overflow-hidden rounded-xl border border-borderStrong bg-black">
            <iframe
              ref={socialFrameRef}
              src={SOCIAL_STUDIO_PATH}
              title="Social post studio"
              className="block h-[86vh] w-full border-0"
            />
          </div>
          <StudioDataBridge iframeRef={socialFrameRef} />
          <p className="mt-3 max-w-4xl text-[11.5px] leading-relaxed text-mutedDim">
            Six formats — leaderboard (city Top&nbsp;10, busiest cities, a user’s top&nbsp;5), venue card
            (score + criteria breakdown + thought tags + peak time), head-to-head, milestone, stamp
            and quote. Every string is an input; “|” forces a line break. Exports at story
            1080×1920, square 1080×1080, feed 1080×1350, wide 1600×900 and both App Store sizes.
            The <b>Live data</b> panel pulls real cities, venues and counts straight from production.
            Copy and toggles persist per format in this browser; dropped photos don’t.
          </p>
        </>
      ) : null}

      {mode === 'flyer' ? (
        <>
          <div className="overflow-hidden rounded-xl border border-borderStrong bg-black">
            <iframe
              ref={flyerFrameRef}
              src={FLYER_STUDIO_PATH}
              title="Flyer studio — posters, table tents, stickers"
              className="block h-[80vh] w-full border-0"
            />
          </div>
          <StudioDataBridge iframeRef={flyerFrameRef} draft={pendingDraft?.studio === 'flyer' ? pendingDraft : null} onDraftDelivered={() => setPendingDraft(null)} />
          <p className="mt-3 max-w-3xl text-[11.5px] leading-relaxed text-mutedDim">
            The full editor: eleven flyer templates, all 8.5×11″ letter at 300 dpi (rate-us,
            game-day poster, where-do-you-rank, ranked-venue poster, any-good, campus teaser,
            Campus Top 5 leaderboard, head-to-head, overrated, tear-off tabs, going-out-tonight),
            three themes
            (Dark / Light / Violet), and a styled QR — squares, rounded or dots, in
            ink/violet/theme/white, plate on or off, any link. <b>Drag anything to move it, double-click text to rewrite it, click once to
            restyle it</b> (size, color, weight) — highlight part of a text while editing and a
            color swatch tints just that part. The Live venue data panel searches production and
            fills in name, rank and score. Edits persist per template in this browser; Reset
            template starts clean. Export downloads print-resolution PNG at the sizes shown.
          </p>
        </>
      ) : null}

      {mode === 'sticker' ? (
        <>
          <div className="overflow-hidden rounded-xl border border-borderStrong bg-black">
            <iframe
              ref={stickerFrameRef}
              src={STICKER_STUDIO_PATH}
              title="Sticker studio — 3×3 inch guerrilla stickers"
              className="block h-[80vh] w-full border-0"
            />
          </div>
          <StudioDataBridge iframeRef={stickerFrameRef} draft={pendingDraft?.studio === 'sticker' ? pendingDraft : null} onDraftDelivered={() => setPendingDraft(null)} />
          <p className="mt-3 max-w-3xl text-[11.5px] leading-relaxed text-mutedDim">
            Twelve 3×3″ (900×900 · 300 dpi) sticker templates — Official, Question, Scan Big,
            Provocation, Cryptic, Bathroom, Mirror Check, The Line, QR Max, The Opinions, Fair
            Warning, and a venue-personalized one the Live venue data panel fills with a real name
            and rank. Same editor as the Flyer studio: drag anything,
            double-click text to rewrite, click once to restyle; QR style/color/plate/link; Dark /
            Light / Violet themes. Print on matte vinyl and scan a printed proof before a bulk order.
          </p>
        </>
      ) : null}

      {mode === 'qr' ? (
        <>
          <div className="overflow-hidden rounded-xl border border-borderStrong bg-black">
            <iframe
              ref={qrFrameRef}
              src={QR_STUDIO_PATH}
              title="QR export — fully customizable, print-ready"
              className="block h-[80vh] w-full border-0"
            />
          </div>
          <StudioDataBridge iframeRef={qrFrameRef} draft={pendingDraft?.studio === 'qr' ? pendingDraft : null} onDraftDelivered={() => setPendingDraft(null)} />
          <p className="mt-3 max-w-3xl text-[11.5px] leading-relaxed text-mutedDim">
            Same editor as the Flyer studio, QR-first: four templates (matchbox back 1.4×2.2″ at
            600&nbsp;dpi, QR + caption, QR + wordmark, QR only) on Black / Dark / Light / Violet /
            Transparent backgrounds. <b>Drag the QR or any text to move it, grab the violet corner
            dot to resize it, double-click text to rewrite it</b> — plus the usual QR knobs (any
            link, squares/rounded/dots, ink/violet/theme/white, plate on/off). Error correction runs
            at maximum here so matchbox-size print stays scannable. Export quality goes to 4× ultra
            (a square matchbox layout exports 3360×3360 — 2400&nbsp;dpi at print size), or send the
            printer “Export QR as SVG” — true vector, the highest quality that exists. Proof-scan a
            printed sample before the bulk order.
          </p>
        </>
      ) : null}

      {mode === 'logo' ? (
        <>
          <div className="overflow-hidden rounded-xl border border-borderStrong bg-black">
            <iframe
              src={LOGO_STUDIO_PATH}
              title="Logo export — the wordmark at any quality"
              className="block h-[80vh] w-full border-0"
            />
          </div>
          <p className="mt-3 max-w-3xl text-[11.5px] leading-relaxed text-mutedDim">
            The wordmark alone, from the traced vector: pick logo color and background (including
            transparent — the checkerboard shows what will be see-through), set the space around the
            word with the slider, and export as PNG at 512px up to 8K, or as true-vector SVG for
            printers. Settings persist in this browser.
          </p>
        </>
      ) : null}

      {mode === 'tent' ? (
        <>
          <div className="overflow-hidden rounded-xl border border-borderStrong bg-black">
            <iframe
              ref={tentFrameRef}
              src={TENT_STUDIO_PATH}
              title="Table tent cards — 18-up cut-out sheet"
              className="block h-[80vh] w-full border-0"
            />
          </div>
          <StudioDataBridge iframeRef={tentFrameRef} draft={pendingDraft?.studio === 'tent' ? pendingDraft : null} onDraftDelivered={() => setPendingDraft(null)} />
          <p className="mt-3 max-w-3xl text-[11.5px] leading-relaxed text-mutedDim">
            Design ONE 6.0×4.2&nbsp;cm card — four templates (rate-us, going-out, this-bar-is-ranked,
            wordmark), the Live venue data panel fills the venue, and the full editor applies. Then
            <b> Preview sheet</b> tiles it eighteen-up across US letter with dashed cut guides and
            exports the sheet as <b>PDF</b> (612×792pt, exact print size) or 600&nbsp;dpi PNG. Print
            at 100% (no &ldquo;fit to page&rdquo;), cut on the dashed lines.
          </p>
        </>
      ) : null}

      {mode === 'flag' ? (
        <>
          <div className="overflow-hidden rounded-xl border border-borderStrong bg-black">
            <iframe
              ref={flagFrameRef}
              src={FLAG_STUDIO_PATH}
              title="Flag studio — flags and fabric banners"
              className="block h-[80vh] w-full border-0"
            />
          </div>
          <StudioDataBridge iframeRef={flagFrameRef} draft={pendingDraft?.studio === 'flag' ? pendingDraft : null} onDraftDelivered={() => setPendingDraft(null)} />
          <p className="mt-3 max-w-3xl text-[11.5px] leading-relaxed text-mutedDim">
            Fabric formats: five templates (classic wordmark, going-out-tonight, rankings-don&rsquo;t-lie,
            2×3 vertical, 6×2 banner) with <b>Flag size</b> toggles — 3×5, 5×3, 2×3, 3×2, 6×2, 2×6&nbsp;ft —
            that resize the canvas and keep the export&rsquo;s dpi tag honest for the printer. Same editor as
            the rest: drag/resize/restyle anything, <b>+ QR code</b> and + text drop new objects on the
            board, Glow, themes, library saves. At 4× quality a 3×5&nbsp;ft flag exports at 160 dpi —
            beyond what fabric printing resolves.
          </p>
        </>
      ) : null}

      {mode === 'reel' ? (
        <>
          <div className="overflow-hidden rounded-xl border border-borderStrong bg-black">
            <iframe
              src={REEL_STUDIO_PATH}
              title="Instagram reel & carousel studio"
              className="block h-[80vh] w-full border-0"
            />
          </div>
          <p className="mt-3 max-w-3xl text-[11.5px] leading-relaxed text-mutedDim">
            Everything on the canvas is a draggable layer. Click one to edit its text, size, weight,
            alignment and colour; arrow keys nudge, Shift nudges further. <b>Presets lay out a whole
            slide at once</b> and carry your clip across, so start from one rather than a blank.
            <b>PNG</b> in the bottom bar exports the current slide as a still, whatever is on it. Exports land in the <b>Exports</b> tray at the bottom of the left rail — click one to save it, since Chrome blocks a page from downloading several files at once. <b>+ Circle</b> and <b>+ Arrow</b> add annotation shapes — colour, thickness, solid/dashed/dotted, fill, arrowhead, rotation and curve — and <b>Forward</b>/<b>Back</b> puts them over or under a recording. <b>⌘C</b>/<b>⌘X</b>/<b>⌘V</b> move layers between slides — and between decks — landing at the same coordinates so placement matches. Every slider's readout is a field you can type into — including text size, and values past where the slider stops. <b>⌘D</b> duplicates the selected layer (or the whole multi-selection). Every layer has a numeric <b>Size</b> in px with an aspect lock you can turn off. <b>+ Image</b> adds a still that crops exactly like a clip, with edge treatments — feather, fade out, vignette, ring, glow, shadow. The picker at the top of the left rail holds as many decks as you like — one per Instagram post. Switching saves where you were, so you can come back to any of them. Drag slides in the left rail to reorder them (or <b>Alt</b>+<b>↑</b>/<b>↓</b>). <b>+ Clip</b> drops another recording placeholder on the slide; select one and press Delete to remove it. Drag its handles to crop the frame — the edge opposite the one you grab stays put — and <b>Alt</b>-drag to slide the recording inside it. For a transparent Rotato export set <b>Backdrop → None</b>: the black you see is the studio's own panel showing through the alpha, not part of the clip. <b>Crop in</b> trims a clip's own edges — Rotato exports pad around the device, so raise
            it until the phone fills the frame. Each clip layer owns its own recording, so every slide can carry a different one — and
            a slide can hold more than one. <b>Double-click any text to edit it in place</b> — Enter forces a line break exactly
            where you want it, Escape or a click away commits. <b>Undo/redo</b> (Cmd Z / Cmd Shift Z), <b>shift-click</b> to multi-select, and align
            buttons that target the canvas for one layer and the group for several. "Reset this
            screen" re-runs the slide's preset without losing the clip.
            <b>+ Icon</b> adds a vector mark — five swipe cues (chevron, double, arrow, hand,
            carousel dots) plus star, bolt, pin and check, each taking the deck's ink colours.
            <b>Box and type are separate.</b> Handles resize the text BOX — corners for both
            dimensions, sides for width; type size stays on the slider, so you can grow a frame
            without growing the words. "Hug content" returns a box to auto height. On a clip, the
            side handles set its aspect. Layers snap to the
            centre, the safe margin and each other. <b>The deck saves itself</b> — including the
            clips, which live in IndexedDB, so a reload brings everything back. Slides with a clip
            export as <b>video</b>, text-only slides as PNG.
            <br />
            Recording happens in real time through the canvas, so an export takes about as long as
            the clip plays. Chrome and Safari both write H.264 MP4 on current versions; the studio
            probes at load and states which container you will get before you record, because
            Instagram refuses WebM.
          </p>
        </>
      ) : null}

      {mode === 'appstore' ? (
        <>
          <div className="overflow-hidden rounded-xl border border-borderStrong bg-black">
            <iframe
              src={APPSTORE_EDITOR_PATH}
              title="App Store screenshot & ad studio"
              className="block h-[80vh] w-full border-0"
            />
          </div>
          <p className="mt-3 max-w-3xl text-[11.5px] leading-relaxed text-mutedDim">
            This is the standalone series editor, embedded whole: six-slide screenshot series
            (click a slide or drag a file onto it, edit headline/subline beneath, “|” forces a line
            break), App Store ad mock-ups below it with editable app name / rating / reviews /
            category, and Export all at exact App Store resolution. Cramped? Use Open full screen.
          </p>
        </>
      ) : null}

      {mode === 'make' ? <MakeAnythingStudio /> : null}

      {mode === 'images' ? <ImageStudio /> : null}

      {mode === 'library' ? (
        <div className="max-w-5xl">
          {libError ? <div className="admin-banner-error mb-4">{libError}</div> : null}
          {libAssets === null ? (
            <SkeletonRows rows={4} />
          ) : libItems.length === 0 ? (
            <EmptyState
              title="Nothing saved yet"
              hint="In the Flyer, Sticker, Venue signage or QR studio, hit “Save to library” next to the export buttons. Designs saved there live here — on any device, not just this browser."
            />
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {libItems.map(({ asset, draft }) => (
                <div key={asset.id} className="admin-card overflow-hidden !p-0">
                  <button
                    type="button"
                    onClick={() => openDraft(asset)}
                    className="block w-full"
                    title="Open in its studio"
                  >
                    {draft?.thumb ? (
                      <img src={draft.thumb} alt="" className="aspect-[4/5] w-full bg-black object-contain" />
                    ) : (
                      <div className="flex aspect-[4/5] w-full items-center justify-center bg-black text-[11px] text-mutedDim">
                        no preview
                      </div>
                    )}
                  </button>
                  <div className="flex items-center justify-between gap-2 p-3">
                    <div className="min-w-0">
                      <div className="truncate text-[13px] font-medium text-white">{asset.title}</div>
                      <div className="text-[11px] text-mutedDim">
                        {draft?.studio === 'flyer'
                          ? 'Flyer studio'
                          : draft?.studio === 'sticker'
                            ? 'Sticker studio'
                            : draft?.studio === 'signage'
                              ? 'Venue signage'
                              : draft?.studio === 'flag'
                                ? 'Flag studio'
                                : draft?.studio === 'tent'
                                  ? 'Table tent'
                                : draft?.studio === 'qr'
                                  ? 'QR export'
                                  : 'Unknown'}
                        {' · '}
                        {new Date(asset.updated_at).toLocaleDateString()}
                        {asset.created_by && libOwners[asset.created_by]
                          ? ` · by @${libOwners[asset.created_by]}`
                          : ''}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      <button
                        type="button"
                        onClick={() => openDraft(asset)}
                        className="admin-btn admin-btn-primary admin-btn-sm"
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const owner = asset.created_by ? libOwners[asset.created_by] : null;
                          if (!window.confirm(`Delete "${asset.title}"${owner ? ` (saved by @${owner})` : ''} from the SHARED library?`)) return;
                          void supabase
                            .rpc('admin_delete_studio_asset', { p_id: asset.id })
                            .then(() => loadLibrary());
                        }}
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        title="Delete from library"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {mode === 'city-top10' ? (
        <p className="mt-4 max-w-2xl text-[11.5px] leading-relaxed text-mutedDim">
          City assets are text-only by design — venue photos from Google/Foursquare can’t be
          reused in marketing.
        </p>
      ) : null}
    </div>
  );
}
